import { DataPlaneAuthService } from 'app/services/data-plane-auth.service';
import { GlobalVariables } from 'app/services/globals';
import { UrlInformationBaseService } from 'app/services/url-information-base.service';
import { MetadataClientFactoryService } from 'app/services/metadata-client/metadata-client-factory.service';
import { IMetadataClient } from 'app/services/base-metadata-client';
import { Http, Headers } from 'app/services/http';
import { HttpMethod } from 'app/interfaces/IHttpClient';
import { IStringMap } from 'app/interfaces/IStringMap';
import { generateUUID } from 'app/shared/utils/uuid.util';
import { LoggerService } from 'app/logger/logger.service';

import { map, catchError, retryWhen } from 'rxjs/operators';
import { convertObjectToString } from 'app/shared/utils/string.util';
import { defaultRetryPolicy } from 'app/shared/utils/http.utils';
import { stringIsNullOrEmpty } from 'shell-models-lib';

export class DataPlaneApiBaseService {
    public static className: string = 'DataPlaneApiBaseService';
    protected _apiVersion: string;
    protected readonly _metadataClient: IMetadataClient;

    public constructor(
        private readonly _httpClient: Http,
        private readonly _dataPlaneAuthService: DataPlaneAuthService,
        private readonly _metadataClientService: MetadataClientFactoryService,
        private readonly _urlSvc: UrlInformationBaseService,
        private readonly _logger: LoggerService
    ) {
        this._metadataClient = this._metadataClientService.getClient(true);
    }

    public async canUseDataPlane(): Promise<boolean> {
        if (!GlobalVariables.enableDataPlane) {
            return false;
        }
        return await this._dataPlaneAuthService.canGetDataPlaneAccess();
    }

    public async sendMessageAsync<TRequest, TResponse>(
        uri: string,
        requestMethod: HttpMethod,
        requestBody?: TRequest,
        additionalQueryParameters?: IStringMap<string>,
        apiVersion: string = this._apiVersion,
        customHeaders?: Headers,
        activityId?: string
    ): Promise<TResponse> {
        let queryParameters = !stringIsNullOrEmpty(apiVersion)
            ? {
                'api-version': apiVersion
            }
            : {};
        queryParameters = {
            ...queryParameters,
            ...additionalQueryParameters
        };
        const requestActivityId = activityId == null ? generateUUID() : activityId;
        const defaultHeaders = await this.getDefaultDataPlaneHeadersAsync(requestActivityId);
        const headers = this._mergeHeaders(defaultHeaders, customHeaders);

        return this._httpClient.request(
            uri, {
            method: requestMethod,
            params: queryParameters,
            body: requestBody,
            headers
        })
            .pipe(
                map((response) => response.json()),
                catchError((e) => {
                    const activityIdMessage = `${stringResources.ActivityId}:${requestActivityId}`;
                    this._logger.warn(DataPlaneApiBaseService.className, `_post - Error while calling ${uri}. Status: ${e.status}. ${activityIdMessage}.`);
                    if (e.status === 504) {
                        const message = `${stringResources.GatewayTimeOutError} ${activityIdMessage}`;
                        this._logger.warn(DataPlaneApiBaseService.className, message);
                        throw message;
                    } else if (e.status === 0) {
                        const message = `${e.statusText}. ${activityIdMessage}`;
                        // When status is 0, we do need to log the error object for more information
                        this._logger.error(DataPlaneApiBaseService.className, `_post - Failed with status 0. Url: ${uri}. Error: ${convertObjectToString(e)}. ${activityIdMessage}`);
                        throw message;
                    } else if (e.status < 500 && e.status >= 200) {
                        // user error
                        if (e._body) {
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            const result = (e._body.error != null ? JSON.parse(e._body.error) : JSON.parse(e._body)) as any;
                            const message = `${result.message || (result.error != null ? result.error.message : '')}. ${activityIdMessage}`;
                            throw message;
                        } else if (e.error) {
                            const content = JSON.parse(e.error);
                            const message = `${content.message != null ? content.message : e.error}. ${activityIdMessage}`;
                            throw message;
                        } else {
                            const message = `${JSON.stringify(e)}. ${activityIdMessage}`;
                            throw message;
                        }
                    } else {
                        // internal server error
                        const message = `${e.statusText}. ${activityIdMessage}`;
                        this._logger.error(DataPlaneApiBaseService.className, message);
                        throw message;
                    }
                })
            )
            .toPromise();
    }

    public async sendMessageGetFullResponseAsync<TRequest>(
        uri: string,
        requestMethod: HttpMethod,
        requestBody?: TRequest,
        additionalQueryParameters?: IStringMap<string>,
        apiVersion: string = this._apiVersion,
        customHeaders?: Headers,
        activityId?: string,
        throwError: boolean = false
    ): Promise<Response> {
        const queryParameters = {
            'api-version': apiVersion ? apiVersion : undefined,
            ...additionalQueryParameters
        };
        const requestActivityId = activityId == null ? generateUUID() : activityId;
        const defaultHeaders = await this.getDefaultDataPlaneHeadersAsync(requestActivityId);
        const headers = this._mergeHeaders(defaultHeaders, customHeaders);
        return this._httpClient.request(
            uri,
            {
                method: requestMethod,
                params: queryParameters,
                body: requestBody,
                headers
            }
        ).pipe(retryWhen(defaultRetryPolicy))
        .toPromise()
        .then(
            (response) => {
                return response;
            },
            (error) => {
                if (throwError) {
                    throw error;
                }
                return error;
            })
            .catch((exception) => {
                throw exception;
            });
    }

    protected async _getDataPlaneBaseUrl(): Promise<string> {
        if (__IS_A365__) {
            return this._metadataClient.getBaseUri();
        } else {
            const baseUrl = (await this._dataPlaneAuthService.getDataPlaneAccess()).dataPlaneUrl;
            return baseUrl + this._urlSvc.getResourceId();
        }
    }

    protected async getDefaultDataPlaneHeadersAsync(activityId: string): Promise<Headers> {
        const headers = new Headers();
        const token = (await this._dataPlaneAuthService.getDataPlaneAccess()).accessToken;
        headers.append('Content-Type', 'application/json');
        headers.append('x-ms-client-request-id', activityId);
        headers.append('x-ms-dataplane-token', token);
        return headers;
    }

    private _mergeHeaders(defaultHeaders: Headers, customHeaders?: Headers): Headers {
        if (!customHeaders) {
            return defaultHeaders;
        }

        customHeaders.keys().forEach((name: string) => {
            const headerVal = customHeaders.get(name);
            if (headerVal != null) {
                defaultHeaders.append(name, headerVal);
            }
        });
        return defaultHeaders;
    }
}
